import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatStandard } from '../utils/standardLabel';
import './Reports.css';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('school'); // 'school' or 'date'
  const [schoolReports, setSchoolReports] = useState([]);
  const [dateReports, setDateReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedSchools, setExpandedSchools] = useState(new Set());
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [summaryStats, setSummaryStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Set default date range to last 30 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    
    fetchSchoolReports();
  }, []);

  useEffect(() => {
    if (activeTab === 'date' && startDate && endDate) {
      fetchDateReports();
    }
  }, [activeTab, startDate, endDate]);

  const fetchSchoolReports = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Fetching school-wise reports...');
      const response = await api.get('/admin/reports/school-wise');
      console.log('School-wise reports response:', response.data);
      
      if (!response.data || !response.data.reports) {
        console.error('Invalid response format:', response.data);
        setError('Invalid response from server. Please try again.');
        setSchoolReports([]);
        setSummaryStats(null);
        return;
      }
      
      const reports = response.data.reports || [];
      console.log('Setting school reports:', reports.length, 'schools');
      setSchoolReports(reports);
      
      // Calculate summary stats
      const stats = {
        totalSchools: reports.length,
        totalOrders: reports.reduce((sum, s) => sum + parseInt(s.total_orders || 0), 0),
        totalParents: reports.reduce((sum, s) => sum + parseInt(s.total_parents || 0), 0),
        totalRevenue: reports.reduce((sum, s) => sum + parseFloat(s.total_revenue || 0), 0),
        paidRevenue: reports.reduce((sum, s) => sum + parseFloat(s.paid_revenue || 0), 0)
      };
      console.log('Summary stats:', stats);
      setSummaryStats(stats);
    } catch (err) {
      console.error('Error fetching school reports:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to load school-wise reports. Please try again.');
      setSchoolReports([]);
      setSummaryStats(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchDateReports = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      console.log('Fetching date-wise reports...', { startDate, endDate });
      const response = await api.get(`/admin/reports/date-wise?${params.toString()}`);
      console.log('Date-wise reports response:', response.data);
      
      if (!response.data || !response.data.reports) {
        console.error('Invalid response format:', response.data);
        setError('Invalid response from server. Please try again.');
        setDateReports([]);
        setSummaryStats(null);
        return;
      }
      
      const reports = response.data.reports || [];
      console.log('Setting date reports:', reports.length, 'days');
      setDateReports(reports);
      
      // Calculate summary stats
      const stats = {
        totalDays: reports.length,
        totalOrders: reports.reduce((sum, d) => sum + parseInt(d.total_orders || 0), 0),
        uniqueCustomers: reports.reduce((sum, d) => sum + parseInt(d.unique_customers || 0), 0),
        totalRevenue: reports.reduce((sum, d) => sum + parseFloat(d.total_revenue || 0), 0),
        paidRevenue: reports.reduce((sum, d) => sum + parseFloat(d.paid_revenue || 0), 0)
      };
      console.log('Summary stats:', stats);
      setSummaryStats(stats);
    } catch (err) {
      console.error('Error fetching date reports:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to load date-wise reports. Please try again.');
      setDateReports([]);
      setSummaryStats(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleSchoolExpanded = (schoolId) => {
    const newExpanded = new Set(expandedSchools);
    if (newExpanded.has(schoolId)) {
      newExpanded.delete(schoolId);
    } else {
      newExpanded.add(schoolId);
    }
    setExpandedSchools(newExpanded);
  };

  const toggleDateExpanded = (date) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const exportSchoolWiseToExcel = () => {
    const data = [];
    
    schoolReports.forEach(school => {
      // School summary row
      data.push({
        'School Name': school.school_name,
        'City': school.city,
        'Board Type': school.board_type,
        'Total Orders': school.total_orders,
        'Total Parents': school.total_parents,
        'Total Revenue': parseFloat(school.total_revenue || 0).toFixed(2),
        'Paid Revenue': parseFloat(school.paid_revenue || 0).toFixed(2),
        'First Order Date': school.first_order_date ? new Date(school.first_order_date).toLocaleDateString() : '',
        'Last Order Date': school.last_order_date ? new Date(school.last_order_date).toLocaleDateString() : '',
        'Order Number': '',
        'Order Date': '',
        'Parent Name': '',
        'Parent Email': '',
        'Parent Mobile': '',
        'Order Status': '',
        'Payment Status': '',
        'Order Amount': '',
        'Delivery Charge': '',
        'Total Amount': '',
        'Kit Name': '',
        'Standard': '',
        'Quantity': '',
        'Item Price': ''
      });

      // Order details
      school.orders.forEach(order => {
        const orderTotal = parseFloat(order.total_amount) + parseFloat(order.delivery_charge);
        const items = Array.isArray(order.items) ? order.items : [];
        
        if (items.length > 0) {
          items.forEach((item, idx) => {
            data.push({
              'School Name': idx === 0 ? school.school_name : '',
              'City': idx === 0 ? school.city : '',
              'Board Type': idx === 0 ? school.board_type : '',
              'Total Orders': idx === 0 ? school.total_orders : '',
              'Total Parents': idx === 0 ? school.total_parents : '',
              'Total Revenue': idx === 0 ? parseFloat(school.total_revenue || 0).toFixed(2) : '',
              'Paid Revenue': idx === 0 ? parseFloat(school.paid_revenue || 0).toFixed(2) : '',
              'First Order Date': '',
              'Last Order Date': '',
              'Order Number': idx === 0 ? order.order_number : '',
              'Order Date': idx === 0 ? new Date(order.created_at).toLocaleDateString() : '',
              'Parent Name': idx === 0 ? order.parent_name : '',
              'Parent Email': idx === 0 ? order.parent_email : '',
              'Parent Mobile': idx === 0 ? order.parent_mobile : '',
              'Order Status': idx === 0 ? order.status : '',
              'Payment Status': idx === 0 ? order.payment_status : '',
              'Order Amount': idx === 0 ? parseFloat(order.total_amount).toFixed(2) : '',
              'Delivery Charge': idx === 0 ? parseFloat(order.delivery_charge).toFixed(2) : '',
              'Total Amount': idx === 0 ? orderTotal.toFixed(2) : '',
              'Kit Name': item.kit_name || '',
              'Standard': formatStandard(item.standard) || '',
              'Quantity': item.quantity || '',
              'Item Price': parseFloat(item.price || 0).toFixed(2)
            });
          });
        } else {
          // Order without items
          data.push({
            'School Name': '',
            'City': '',
            'Board Type': '',
            'Total Orders': '',
            'Total Parents': '',
            'Total Revenue': '',
            'Paid Revenue': '',
            'First Order Date': '',
            'Last Order Date': '',
            'Order Number': order.order_number,
            'Order Date': new Date(order.created_at).toLocaleDateString(),
            'Parent Name': order.parent_name,
            'Parent Email': order.parent_email,
            'Parent Mobile': order.parent_mobile,
            'Order Status': order.status,
            'Payment Status': order.payment_status,
            'Order Amount': parseFloat(order.total_amount).toFixed(2),
            'Delivery Charge': parseFloat(order.delivery_charge).toFixed(2),
            'Total Amount': orderTotal.toFixed(2),
            'Kit Name': '',
            'Standard': '',
            'Quantity': '',
            'Item Price': ''
          });
        }
      });
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'School-Wise Reports');
    XLSX.writeFile(wb, `School-Wise-Orders-Report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportDateWiseToExcel = () => {
    const data = [];
    
    dateReports.forEach(dateReport => {
      // Date summary row
      data.push({
        'Order Date': new Date(dateReport.order_date).toLocaleDateString(),
        'Total Orders': dateReport.total_orders,
        'Unique Customers': dateReport.unique_customers,
        'Schools Ordered': dateReport.schools_ordered,
        'Total Revenue': parseFloat(dateReport.total_revenue || 0).toFixed(2),
        'Paid Revenue': parseFloat(dateReport.paid_revenue || 0).toFixed(2),
        'Pending Revenue': parseFloat(dateReport.pending_revenue || 0).toFixed(2),
        'Delivered Orders': dateReport.delivered_orders,
        'Pending Orders': dateReport.pending_orders,
        'Confirmed Orders': dateReport.confirmed_orders,
        'Order Number': '',
        'Order Time': '',
        'Parent Name': '',
        'Parent Email': '',
        'Parent Mobile': '',
        'School Name': '',
        'School City': '',
        'Order Status': '',
        'Payment Status': '',
        'Order Amount': '',
        'Delivery Charge': '',
        'Total Amount': '',
        'Kit Name': '',
        'Standard': '',
        'Quantity': '',
        'Item Price': ''
      });

      // Order details
      dateReport.orders.forEach(order => {
        const orderTotal = parseFloat(order.total_amount) + parseFloat(order.delivery_charge);
        const items = Array.isArray(order.items) ? order.items : [];
        
        if (items.length > 0) {
          items.forEach((item, idx) => {
            data.push({
              'Order Date': idx === 0 ? new Date(dateReport.order_date).toLocaleDateString() : '',
              'Total Orders': idx === 0 ? dateReport.total_orders : '',
              'Unique Customers': idx === 0 ? dateReport.unique_customers : '',
              'Schools Ordered': idx === 0 ? dateReport.schools_ordered : '',
              'Total Revenue': idx === 0 ? parseFloat(dateReport.total_revenue || 0).toFixed(2) : '',
              'Paid Revenue': idx === 0 ? parseFloat(dateReport.paid_revenue || 0).toFixed(2) : '',
              'Pending Revenue': idx === 0 ? parseFloat(dateReport.pending_revenue || 0).toFixed(2) : '',
              'Delivered Orders': idx === 0 ? dateReport.delivered_orders : '',
              'Pending Orders': idx === 0 ? dateReport.pending_orders : '',
              'Confirmed Orders': idx === 0 ? dateReport.confirmed_orders : '',
              'Order Number': idx === 0 ? order.order_number : '',
              'Order Time': idx === 0 ? new Date(order.created_at).toLocaleTimeString() : '',
              'Parent Name': idx === 0 ? order.parent_name : '',
              'Parent Email': idx === 0 ? order.parent_email : '',
              'Parent Mobile': idx === 0 ? order.parent_mobile : '',
              'School Name': idx === 0 ? order.school_name : '',
              'School City': idx === 0 ? order.school_city : '',
              'Order Status': idx === 0 ? order.status : '',
              'Payment Status': idx === 0 ? order.payment_status : '',
              'Order Amount': idx === 0 ? parseFloat(order.total_amount).toFixed(2) : '',
              'Delivery Charge': idx === 0 ? parseFloat(order.delivery_charge).toFixed(2) : '',
              'Total Amount': idx === 0 ? orderTotal.toFixed(2) : '',
              'Kit Name': item.kit_name || '',
              'Standard': formatStandard(item.standard) || '',
              'Quantity': item.quantity || '',
              'Item Price': parseFloat(item.price || 0).toFixed(2)
            });
          });
        } else {
          // Order without items
          data.push({
            'Order Date': '',
            'Total Orders': '',
            'Unique Customers': '',
            'Schools Ordered': '',
            'Total Revenue': '',
            'Paid Revenue': '',
            'Pending Revenue': '',
            'Delivered Orders': '',
            'Pending Orders': '',
            'Confirmed Orders': '',
            'Order Number': order.order_number,
            'Order Time': new Date(order.created_at).toLocaleTimeString(),
            'Parent Name': order.parent_name,
            'Parent Email': order.parent_email,
            'Parent Mobile': order.parent_mobile,
            'School Name': order.school_name,
            'School City': order.school_city,
            'Order Status': order.status,
            'Payment Status': order.payment_status,
            'Order Amount': parseFloat(order.total_amount).toFixed(2),
            'Delivery Charge': parseFloat(order.delivery_charge).toFixed(2),
            'Total Amount': orderTotal.toFixed(2),
            'Kit Name': '',
            'Standard': '',
            'Quantity': '',
            'Item Price': ''
          });
        }
      });
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Date-Wise Reports');
    XLSX.writeFile(wb, `Date-Wise-Orders-Report-${startDate}-to-${endDate}.xlsx`);
  };

  if (loading && schoolReports.length === 0 && dateReports.length === 0) {
    return (
      <div className="App">
        <Header />
        <div className="reports-page">
          <div className="container">
            <div className="reports-loading">
              <div className="loading-spinner"></div>
              <p>Loading reports...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="reports-page">
        <div className="container">
          {/* Header Section */}
          <div className="reports-header">
            <div className="reports-header-content">
              <div className="reports-icon-wrapper">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 3v18h18"></path>
                  <path d="M18 7c0 1.657-1.343 3-3 3s-3-1.343-3-3 1.343-3 3-3 3 1.343 3 3z"></path>
                  <path d="M6 21V11l6-6 6 6v10"></path>
                </svg>
              </div>
              <div className="reports-title-section">
                <h1 className="reports-title">Order Reports</h1>
                <p className="reports-subtitle">Comprehensive insights into your orders, revenue, and customer data</p>
              </div>
            </div>
            {(schoolReports.length > 0 || dateReports.length > 0) && (
              <button
                onClick={activeTab === 'school' ? exportSchoolWiseToExcel : exportDateWiseToExcel}
                className="btn-export-excel-modern"
              >
                <div className="export-icon-wrapper">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </div>
                <div className="export-content">
                  <span className="export-title">Export to Excel</span>
                  <span className="export-subtitle">Download complete report data</span>
                </div>
              </button>
            )}
          </div>

          {error && (
            <div className="reports-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <div className="error-content">
                <span>{error}</span>
                {error.includes('Admin access required') && (
                  <div className="error-help">
                    <strong>Solution:</strong> Please logout and login again to refresh your permissions.
                  </div>
                )}
                {(error.includes('401') || error.includes('403')) && (
                  <div className="error-help">
                    <strong>Solution:</strong> Please make sure you are logged in as an admin user.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary Statistics Cards */}
          {summaryStats && (
            <div className="reports-summary-cards">
              <div className="summary-card">
                <h3>{activeTab === 'school' ? 'Total Schools' : 'Total Days'}</h3>
                <p className="summary-stat-number">
                  {activeTab === 'school' ? summaryStats.totalSchools : summaryStats.totalDays}
                </p>
              </div>
              <div className="summary-card">
                <h3>Total Orders</h3>
                <p className="summary-stat-number">{summaryStats.totalOrders}</p>
              </div>
              <div className="summary-card">
                <h3>{activeTab === 'school' ? 'Total Parents' : 'Unique Customers'}</h3>
                <p className="summary-stat-number">
                  {activeTab === 'school' ? summaryStats.totalParents : summaryStats.uniqueCustomers}
                </p>
              </div>
              <div className="summary-card">
                <h3>Total Revenue</h3>
                <p className="summary-stat-number">₹{summaryStats.totalRevenue.toFixed(2)}</p>
              </div>
              <div className="summary-card">
                <h3>Paid Revenue</h3>
                <p className="summary-stat-number">₹{summaryStats.paidRevenue.toFixed(2)}</p>
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="reports-tabs-modern">
            <button
              className={`tab-button-modern ${activeTab === 'school' ? 'active' : ''}`}
              onClick={() => setActiveTab('school')}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
              </div>
              <div className="tab-content">
                <span className="tab-title">School-Wise</span>
                <span className="tab-subtitle">Orders by school</span>
              </div>
            </button>
            <button
              className={`tab-button-modern ${activeTab === 'date' ? 'active' : ''}`}
              onClick={() => setActiveTab('date')}
            >
              <div className="tab-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              <div className="tab-content">
                <span className="tab-title">Date-Wise</span>
                <span className="tab-subtitle">Orders by date</span>
              </div>
            </button>
          </div>

          {/* Date Range Filter for Date-Wise Reports */}
          {activeTab === 'date' && (
            <div className="date-filter-section-modern">
              <div className="filter-header">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="M21 21l-4.35-4.35"></path>
                </svg>
                <span>Filter by Date Range</span>
              </div>
              <div className="filter-controls">
                <div className="filter-input-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="filter-date-input"
                  />
                </div>
                <div className="filter-separator">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14"></path>
                  </svg>
                </div>
                <div className="filter-input-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="filter-date-input"
                  />
                </div>
                <button onClick={fetchDateReports} className="btn-filter-modern">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="M21 21l-4.35-4.35"></path>
                  </svg>
                  <span>Apply Filter</span>
                </button>
              </div>
            </div>
          )}

          {/* School-Wise Reports */}
          {activeTab === 'school' && (
            <div className="reports-content-modern">
              {schoolReports.length === 0 ? (
                <div className="no-reports-modern">
                  <div className="no-reports-icon">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                    </svg>
                  </div>
                  <h3>No School Reports Available</h3>
                  <p>No orders have been placed yet. Reports will appear here once orders are created.</p>
                </div>
              ) : (
                <div className="school-reports-grid">
                  {schoolReports.map((school, index) => (
                    <div 
                      key={school.school_id} 
                      className="school-report-card-modern"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="school-card-header-modern">
                        <div className="school-card-main">
                          <div className="school-badge-modern">
                            <span>{school.board_type}</span>
                          </div>
                          <h3 className="school-name-modern">{school.school_name}</h3>
                          <div className="school-location-modern">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                              <circle cx="12" cy="10" r="3"></circle>
                            </svg>
                            <span>{school.city}</span>
                          </div>
                        </div>
                        <button 
                          className="expand-button-modern"
                          onClick={() => toggleSchoolExpanded(school.school_id)}
                        >
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{ transform: expandedSchools.has(school.school_id) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                      </div>

                      <div className="school-stats-modern">
                        <div className="school-stat-item">
                          <div className="stat-icon-wrapper stat-icon-orders">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </div>
                          <div className="stat-details">
                            <span className="stat-number">{school.total_orders}</span>
                            <span className="stat-label">Orders</span>
                          </div>
                        </div>
                        <div className="school-stat-item">
                          <div className="stat-icon-wrapper stat-icon-parents">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </div>
                          <div className="stat-details">
                            <span className="stat-number">{school.total_parents}</span>
                            <span className="stat-label">Parents</span>
                          </div>
                        </div>
                        <div className="school-stat-item">
                          <div className="stat-icon-wrapper stat-icon-revenue">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="1" x2="12" y2="23"></line>
                              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                            </svg>
                          </div>
                          <div className="stat-details">
                            <span className="stat-number">₹{parseFloat(school.total_revenue || 0).toFixed(0)}</span>
                            <span className="stat-label">Revenue</span>
                          </div>
                        </div>
                      </div>

                      {expandedSchools.has(school.school_id) && (
                        <div className="school-orders-expanded">
                          <div className="expanded-header">
                            <span className="expanded-title">Order Details</span>
                            <span className="expanded-count">{school.orders.length} orders</span>
                          </div>
                          <div className="orders-list-expanded">
                            {school.orders.map((order) => (
                              <div key={order.id} className="order-card-expanded">
                                <div className="order-header-expanded">
                                  <div className="order-number-section">
                                    <span className="order-number-badge">#{order.order_number}</span>
                                    <span className="order-date-time">
                                      {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString()}
                                    </span>
                                  </div>
                                  <div className="order-status-badges-expanded">
                                    <span className={`status-badge-expanded status-${order.status.toLowerCase()}`}>
                                      {order.status}
                                    </span>
                                    <span className={`status-badge-expanded status-payment-${order.payment_status.toLowerCase()}`}>
                                      {order.payment_status}
                                    </span>
                                  </div>
                                </div>
                                <div className="order-parent-info-expanded">
                                  <div className="parent-details">
                                    <strong>{order.parent_name}</strong>
                                    <span>{order.parent_email}</span>
                                    <span>{order.parent_mobile}</span>
                                  </div>
                                </div>
                                <div className="order-items-expanded">
                                  {Array.isArray(order.items) && order.items.length > 0 ? (
                                    order.items.map((item, idx) => (
                                      <div key={idx} className="order-item-expanded">
                                        <div className="item-info-expanded">
                                          <span className="item-name-expanded">{item.kit_name}</span>
                                          <span className="item-standard-expanded">{formatStandard(item.standard)}</span>
                                        </div>
                                        <div className="item-quantity-expanded">Qty: {item.quantity}</div>
                                        <div className="item-price-expanded">₹{parseFloat(item.price).toFixed(2)}</div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="no-items">No items found</div>
                                  )}
                                </div>
                                <div className="order-total-expanded">
                                  <span>Total: ₹{(parseFloat(order.total_amount) + parseFloat(order.delivery_charge)).toFixed(2)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Date-Wise Reports */}
          {activeTab === 'date' && (
            <div className="reports-content-modern">
              {dateReports.length === 0 ? (
                <div className="no-reports-modern">
                  <div className="no-reports-icon">
                    <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                  </div>
                  <h3>No Date Reports Available</h3>
                  <p>No orders found for the selected date range. Try adjusting your filters.</p>
                </div>
              ) : (
                <div className="date-reports-grid">
                  {dateReports.map((dateReport, index) => (
                    <div 
                      key={dateReport.order_date} 
                      className="date-report-card-modern"
                      style={{ animationDelay: `${index * 0.08}s` }}
                    >
                      <div className="date-card-header-modern">
                        <div className="date-card-main">
                          <div className="date-badge-modern">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                          </div>
                          <h3 className="date-title-modern">
                            {new Date(dateReport.order_date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </h3>
                        </div>
                        <button 
                          className="expand-button-modern"
                          onClick={() => toggleDateExpanded(dateReport.order_date)}
                        >
                          <svg 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2"
                            style={{ transform: expandedDates.has(dateReport.order_date) ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          >
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </button>
                      </div>

                      <div className="date-stats-modern">
                        <div className="date-stat-item">
                          <div className="stat-icon-wrapper stat-icon-orders">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                            </svg>
                          </div>
                          <div className="stat-details">
                            <span className="stat-number">{dateReport.total_orders}</span>
                            <span className="stat-label">Orders</span>
                          </div>
                        </div>
                        <div className="date-stat-item">
                          <div className="stat-icon-wrapper stat-icon-customers">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                              <circle cx="9" cy="7" r="4"></circle>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                            </svg>
                          </div>
                          <div className="stat-details">
                            <span className="stat-number">{dateReport.unique_customers}</span>
                            <span className="stat-label">Customers</span>
                          </div>
                        </div>
                        <div className="date-stat-item">
                          <div className="stat-icon-wrapper stat-icon-schools">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                            </svg>
                          </div>
                          <div className="stat-details">
                            <span className="stat-number">{dateReport.schools_ordered}</span>
                            <span className="stat-label">Schools</span>
                          </div>
                        </div>
                        <div className="date-stat-item">
                          <div className="stat-icon-wrapper stat-icon-revenue">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="12" y1="1" x2="12" y2="23"></line>
                              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                            </svg>
                          </div>
                          <div className="stat-details">
                            <span className="stat-number">₹{parseFloat(dateReport.total_revenue || 0).toFixed(0)}</span>
                            <span className="stat-label">Revenue</span>
                          </div>
                        </div>
                      </div>

                      {expandedDates.has(dateReport.order_date) && (
                        <div className="date-orders-expanded">
                          <div className="expanded-header">
                            <span className="expanded-title">Order Details</span>
                            <span className="expanded-count">{dateReport.orders.length} orders</span>
                          </div>
                          <div className="orders-list-expanded">
                            {dateReport.orders.map((order) => (
                              <div key={order.id} className="order-card-expanded">
                                <div className="order-header-expanded">
                                  <div className="order-number-section">
                                    <span className="order-number-badge">#{order.order_number}</span>
                                    <span className="order-date-time">
                                      {new Date(order.created_at).toLocaleTimeString()}
                                    </span>
                                  </div>
                                  <div className="order-status-badges-expanded">
                                    <span className={`status-badge-expanded status-${order.status.toLowerCase()}`}>
                                      {order.status}
                                    </span>
                                    <span className={`status-badge-expanded status-payment-${order.payment_status.toLowerCase()}`}>
                                      {order.payment_status}
                                    </span>
                                  </div>
                                </div>
                                <div className="order-parent-info-expanded">
                                  <div className="parent-details">
                                    <strong>{order.parent_name}</strong>
                                    <span>{order.parent_email}</span>
                                    <span>{order.parent_mobile}</span>
                                    {order.school_name && (
                                      <span className="school-info-expanded">
                                        {order.school_name} - {order.school_city}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="order-items-expanded">
                                  {Array.isArray(order.items) && order.items.length > 0 ? (
                                    order.items.map((item, idx) => (
                                      <div key={idx} className="order-item-expanded">
                                        <div className="item-info-expanded">
                                          <span className="item-name-expanded">{item.kit_name}</span>
                                          <span className="item-standard-expanded">{formatStandard(item.standard)}</span>
                                        </div>
                                        <div className="item-quantity-expanded">Qty: {item.quantity}</div>
                                        <div className="item-price-expanded">₹{parseFloat(item.price).toFixed(2)}</div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="no-items">No items found</div>
                                  )}
                                </div>
                                <div className="order-total-expanded">
                                  <span>Total: ₹{(parseFloat(order.total_amount) + parseFloat(order.delivery_charge)).toFixed(2)}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Reports;
